import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { ChevronDown, CircleDollarSign, CircleUserRound, Home, Menu, Moon, Package, Search, ShoppingCart, Store, Sun } from "lucide-react";
import { Button } from "@/components/ui/button"; import { Sheet,SheetContent,SheetHeader,SheetTitle,SheetTrigger } from "@/components/ui/sheet";
import { Brand } from "./brand"; import { useStore } from "@/lib/store-context";
import { currencies, currencySymbols, type Currency } from "@/lib/pricing";
import { DropdownMenu,DropdownMenuContent,DropdownMenuItem,DropdownMenuLabel,DropdownMenuRadioGroup,DropdownMenuRadioItem,DropdownMenuSeparator,DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FloatingSupport,SupportActions } from "./support-actions";
const nav = [
  { to: "/products", label: "All Products" },
  { to: "/products/category/streaming", label: "Subscriptions" },
  { to: "/products/category/software", label: "Software" },
  { to: "/products/category/gaming", label: "Gaming" },
  { to: "/support", label: "Support" },
];
export function SiteShell({ children }: { children: ReactNode }) {
  const { count, language, setLanguage, currency, setCurrency, theme, setTheme } = useStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void navigate({ to: "/products", search: { q: search } as any });
  };
  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto grid h-16 max-w-7xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1 px-2 sm:gap-4 sm:px-6">
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[86%]">
                <SheetHeader>
                  <SheetTitle>
                    <Brand />
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 grid gap-1">
                  {nav.map((n) => (
                    <Link key={n.label} to={n.to} className="rounded-md px-3 py-3 font-semibold hover:bg-accent">
                      {n.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
          <span className="min-w-0 overflow-hidden [&_a]:min-w-0 [&_span]:truncate">
            <Brand />
          </span>
          <form onSubmit={submit} className="mx-auto hidden max-w-xl flex-1 md:flex">
            <label className="sr-only" htmlFor="site-search">
              Search products
            </label>
            <div className="flex w-full overflow-hidden rounded-md border border-input bg-secondary/50 focus-within:ring-2 focus-within:ring-ring">
              <Search className="ml-3 mt-2.5 size-4 text-muted-foreground" />
              <input
                id="site-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subscriptions, software, codes..."
                className="h-10 min-w-0 flex-1 bg-transparent px-3 text-sm outline-none"
              />
              <Button type="submit" className="h-10 rounded-none px-5">
                Search
              </Button>
            </div>
          </form>
          <div className="ml-auto flex shrink-0 items-center gap-0 sm:gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1 px-1.5 text-xs sm:px-2" aria-label={`Choose currency, currently ${currency}`}>
                  <CircleDollarSign />
                  <span className="hidden sm:inline">{currency}</span>
                  <ChevronDown className="hidden size-3 sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Display currency</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                  {currencies.map((c) => (
                    <DropdownMenuRadioItem key={c} value={c}>
                      {currencySymbols[c]} {c}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <p className="px-2 py-1 text-[10px] text-muted-foreground">Checkout settles in BDT</p>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Choose appearance">
                  {theme === "dark" ? <Moon /> : <Sun />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Appearance</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as "light" | "dark" | "system")}>
                  <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" className="hidden px-2 text-xs sm:inline-flex" onClick={() => setLanguage(language === "EN" ? "BN" : "EN")} aria-label="Change language">
              {language}
              <ChevronDown className="size-3" />
            </Button>
            <Button asChild variant="ghost" size="icon">
              <Link to="/account" aria-label="Account">
                <CircleUserRound />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="icon" className="relative">
              <Link to="/cart" aria-label={`Cart with ${count} items`}>
                <ShoppingCart />
                {count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 grid size-4 place-items-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    {count}
                  </span>
                )}
              </Link>
            </Button>
          </div>
        </div>
        <div className="border-t border-border/70">
          <nav className="mx-auto hidden h-11 max-w-7xl items-center gap-7 px-6 text-sm font-semibold md:flex">
            {nav.map((n) => (
              <Link key={n.label} to={n.to} className="text-muted-foreground transition-colors hover:text-primary" activeProps={{ className: "text-primary" }}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <footer className="mt-10 border-t bg-secondary/40 text-foreground">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-12 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Brand />
            <p className="mt-4 max-w-sm text-sm leading-6 text-muted-foreground">
              Premium digital products with clear delivery, flexible currency display and reliable order support worldwide.
            </p>
            <div className="mt-5">
              <SupportActions compact />
            </div>
            <p className="mt-5 text-sm font-bold">Product by Digital Growth</p>
            <p className="mt-1 text-xs text-muted-foreground">support@betteraccess.store</p>
          </div>
          <FooterCol title="Shop" items={[["All products", "/products"], ["Subscriptions", "/products/category/streaming"], ["Software", "/products/category/software"], ["AI tools", "/products/category/ai-tools"]]} />
          <FooterCol title="Customer care" items={[["Support center", "/support"], ["My orders", "/account/orders"], ["Refund policy", "/refund-policy"], ["Account", "/account"]]} />
          <FooterCol title="Legal & company" items={[["Terms & conditions", "/terms"], ["Privacy policy", "/privacy"], ["Refund policy", "/refund-policy"], ["Admin preview", "/admin"]]} />
        </div>
        <div className="border-t">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 px-5 py-5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>© 2026 betteraccess.store</span>
            <strong className="text-foreground">Product by Digital Growth</strong>
          </div>
        </div>
      </footer>
      <FloatingSupport />
      <nav className="fixed inset-x-0 bottom-0 z-50 grid h-16 grid-cols-4 border-t bg-background md:hidden">
        <MobileLink to="/" label="Home" icon={<Home />} />
        <MobileLink to="/products" label="Shop" icon={<Store />} />
        <MobileLink to="/account/orders" label="Orders" icon={<Package />} />
        <MobileLink to="/cart" label="Cart" icon={<ShoppingCart />} badge={count} />
      </nav>
    </div>
  );
}

function MobileLink({ to, label, icon, badge }: { to: string; label: string; icon: ReactNode; badge?: number }) {
  return (
    <Link to={to} className="relative flex flex-col items-center justify-center gap-1 text-[10px] font-semibold text-muted-foreground" activeProps={{ className: "text-primary" }}>
      <span className="relative [&_svg]:size-5">
        {icon}
        {!!badge && <span className="absolute -right-3 -top-2 grid size-4 place-items-center rounded-full bg-primary text-[9px] text-primary-foreground">{badge}</span>}
      </span>
      {label}
    </Link>
  );
}

function FooterCol({ title, items }: { title: string; items: string[][] }) {
  return (
    <div>
      <h2 className="mb-4 text-sm font-bold">{title}</h2>
      <ul className="space-y-3 text-sm text-muted-foreground">
        {items.map(([l, h]) => (
          <li key={l}>
            <a href={h} className="transition-colors hover:text-primary">{l}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
